import { api } from "@/services/auth";

// === Types ===
export type ClientType = 'CLIENT' | 'SUPPLIER' | 'PARTNER' | 'CORRESPONDENT' | 'PROSPECT';

export const CLIENT_TYPE_LABELS: Record<ClientType, string> = {
  CLIENT: 'Cliente',
  SUPPLIER: 'Fornecedor',
  PARTNER: 'Parceiro',
  CORRESPONDENT: 'Correspondente',
  PROSPECT: 'Lead',
};

export const CLIENT_TYPE_COLORS: Record<ClientType, string> = {
  CLIENT: 'emerald',
  SUPPLIER: 'amber',
  PARTNER: 'violet',
  CORRESPONDENT: 'sky',
  PROSPECT: 'slate',
};

export type CadastroClient = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  document: string | null;
  type: ClientType;
  created_at: string;
  updated_at: string;
  contacts_count: number;
  ip_cases_count: number;
  pi_types: string[];
  primary_pi_type: string;
};

export type PaginatedClientsResponse = {
  items: CadastroClient[];
  page: number;
  pageSize: number;
  total: number;
};

export type CadastrosDashboardData = {
  kpis: {
    totalEmpresas: number;
    clientes: number;
    fornecedores: number;
    correspondentes: number;
    parceiros: number;
    leads: number;
    semProcessoPI: number;
  };
  recentClients: Array<{
    id: string;
    name: string;
    document: string | null;
    email: string | null;
    phone: string | null;
    type: ClientType;
    created_at: string;
  }>;
};

export type ClientDetail = CadastroClient & {
  contacts: any[];
  ip_cases: any[];
  ip_cases_by_domain: Record<string, any[]>;
  demands_count: number;
};

export type IpCaseEnriched = {
  id: string;
  domain: string;
  code: string;
  title: string | null;
  status: string | null;
  client_id: string | null;
  inpi: any | null;
};

// Empty state — no fake data
const fallbackData: CadastrosDashboardData = {
  kpis: {
    totalEmpresas: 0,
    clientes: 0,
    fornecedores: 0,
    correspondentes: 0,
    parceiros: 0,
    leads: 0,
    semProcessoPI: 0,
  },
  recentClients: [],
};

type CadastrosDashboardApiResponse = {
  kpis?: {
    totalEmpresas?: number;
    clientes?: number;
    fornecedores?: number;
    correspondentes?: number;
    parceiros?: number;
    leads?: number;
    semProcessoPI?: number;
  };
  recentClients?: Array<{
    id: string;
    name: string;
    document?: string | null;
    email?: string | null;
    phone?: string | null;
    type?: string;
    created_at?: string;
  }>;
};

function adaptDashboard(payload: unknown): CadastrosDashboardData {
  if (!payload || typeof payload !== "object") return fallbackData;
  const obj = payload as CadastrosDashboardApiResponse;

  const kpis = obj.kpis;
  return {
    kpis: {
      totalEmpresas: Number(kpis?.totalEmpresas ?? 0),
      clientes: Number(kpis?.clientes ?? 0),
      fornecedores: Number(kpis?.fornecedores ?? 0),
      correspondentes: Number(kpis?.correspondentes ?? 0),
      parceiros: Number(kpis?.parceiros ?? 0),
      leads: Number(kpis?.leads ?? 0),
      semProcessoPI: Number(kpis?.semProcessoPI ?? 0),
    },
    recentClients: Array.isArray(obj.recentClients)
      ? obj.recentClients.map((c) => ({
          id: String(c.id),
          name: String(c.name || ""),
          document: c.document ?? null,
          email: c.email ?? null,
          phone: c.phone ?? null,
          type: (c.type as ClientType) || "CLIENT",
          created_at: String(c.created_at || new Date().toISOString()),
        }))
      : [],
  };
}

function extractClientItems(payload: unknown): CadastroClient[] {
  if (Array.isArray(payload)) {
    return payload as CadastroClient[];
  }

  if (payload && typeof payload === "object" && Array.isArray((payload as { items?: unknown[] }).items)) {
    return (payload as { items: CadastroClient[] }).items;
  }

  return [];
}

function normalizeClientList(payload: unknown): PaginatedClientsResponse {
  const items = extractClientItems(payload);
  const page = Number((payload as { page?: unknown })?.page ?? 1);
  const rawPageSize = (payload as { pageSize?: unknown })?.pageSize;
  const pageSize = Number(rawPageSize ?? items.length ?? 20);
  const total = Number((payload as { total?: unknown })?.total ?? items.length);

  return {
    items,
    page: Number.isFinite(page) && page > 0 ? page : 1,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 20,
    total: Number.isFinite(total) && total >= 0 ? total : items.length,
  };
}
// === Service ===
export const cadastrosService = {
  getFallback: (): CadastrosDashboardData => fallbackData,

  // Dashboard
  getDashboard: async (): Promise<CadastrosDashboardData> => {
    try {
      const { data } = await api.get("/v2/cadastros/dashboard");
      return adaptDashboard(data);
    } catch {
      return fallbackData;
    }
  },

  listClients: async (params?: {
    type?: string;
    q?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedClientsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.set('type', params.type);
    if (params?.search) queryParams.set('q', params.search);
    else if (params?.q) queryParams.set('q', params.q);
    queryParams.set('page', String(params?.page ?? 1));
    queryParams.set('pageSize', String(params?.pageSize ?? 100));
    const qs = queryParams.toString();
    const { data } = await api.get(`/v2/clientes${qs ? `?${qs}` : ''}`);
    return normalizeClientList(data);
  },

  // List clients with optional filters
  getClients: async (params?: {
    type?: string;
    q?: string;
    page?: number;
    pageSize?: number;
  }): Promise<CadastroClient[]> => {
    const response = await cadastrosService.listClients(params);
    return response.items;
  },

  // Get single client detail
  getClient: async (id: string): Promise<ClientDetail> => {
    const { data } = await api.get(`/clients/${id}`);
    return data;
  },

  // Create client
  createClient: async (payload: {
    name: string;
    email?: string;
    document?: string;
    phone?: string;
    type?: ClientType;
    piTypes?: string[];
    country_code?: string;
  }): Promise<any> => {
    const { data } = await api.post("/v2/clientes", payload);
    return data;
  },

  // Update client
  updateClient: async (id: string, payload: Partial<{
    name: string;
    email: string;
    document: string;
    phone: string;
    type: ClientType;
  }>): Promise<any> => {
    const { data } = await api.patch(`/clients/${id}`, payload);
    return data;
  },

  // CNPJ autofill
  autofillCnpj: async (cnpj: string) => {
    const normalizedCnpj = cnpj.replace(/\D/g, '');

    try {
      const { data } = await api.get(`/clients/cnpj/${normalizedCnpj}/autofill`);
      return data;
    } catch (error: any) {
      const status = error?.response?.status;

      // Some deployed environments do not expose the internal autofill route yet,
      // and when both server-side upstreams are unreachable the API responde 5xx.
      // Fallback to BrasilAPI so the cadastro flow keeps working instead of hard failing.
      if (status === 404 || status === 405 || status >= 500 || !status) {
        const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${normalizedCnpj}`);

        if (!response.ok) {
          throw error;
        }

        const data: any = await response.json();
        return {
          cnpj: normalizedCnpj,
          legalName: data?.razao_social || data?.nome_fantasia || "",
          tradeName: data?.nome_fantasia || "",
          email: data?.email || "",
          phone: data?.ddd_telefone_1 || "",
          city: data?.municipio || "",
          state: data?.uf || "",
          mainCnae: data?.cnae_fiscal_descricao || "",
          raw: data,
        };
      }

      throw error;
    }
  },

  // IP Case linking
  getClientIpCases: async (id: string, domain?: string): Promise<{ cases: IpCaseEnriched[] }> => {
    const qs = domain ? `?domain=${domain}` : '';
    const { data } = await api.get(`/clients/${id}/ip-cases${qs}`);
    return data;
  },

  linkIpCases: async (clientId: string, caseIds: string[]): Promise<{ linked: number }> => {
    const { data } = await api.put(`/clients/${clientId}/ip-cases`, { caseIds });
    return data;
  },

  unlinkIpCase: async (clientId: string, caseId: string): Promise<void> => {
    await api.delete(`/clients/${clientId}/ip-cases/${caseId}`);
  },

  // Exclusão de cadastro (QA observação 3): bloqueada com 409 quando o
  // cliente tem patentes no portfólio — a mensagem do backend é exibida.
  deleteClient: async (clientId: string): Promise<{ deleted: boolean; id: string }> => {
    const { data } = await api.delete(`/clients/${clientId}`);
    return data;
  },

  // Search available (unlinked) IpCases
  searchAvailableCases: async (q: string, domain?: string): Promise<any[]> => {
    const params = new URLSearchParams({ q });
    if (domain) params.set('domain', domain);
    const { data } = await api.get(`/ip-cases/available?${params.toString()}`);
    return data?.cases || [];
  },

  // === Contact CRUD ===
  createContact: async (clientId: string, payload: {
    name: string;
    email: string;
    phone?: string;
    role_area?: string;
    is_primary?: boolean;
    notes?: string;
  }): Promise<any> => {
    const { data } = await api.post(`/clients/${clientId}/contacts`, payload);
    return data;
  },

  updateContact: async (clientId: string, contactId: string, payload: {
    name?: string;
    email?: string;
    phone?: string;
    role_area?: string;
    is_primary?: boolean;
    notes?: string;
  }): Promise<any> => {
    const { data } = await api.patch(`/clients/${clientId}/contacts/${contactId}`, payload);
    return data;
  },

  // === Process Creation ===
  lookupNationalProcess: async (payload: {
    code: string;
    domain: string;
    process_number?: string;
    client_id?: string;
    title?: string;
    status?: string;
    monitor_process?: boolean;
    monitor_collision?: boolean;
    monitor_market?: boolean;
    monitoring_types?: string[];
    is_third_party?: boolean;
    notes?: string;
  }): Promise<{ case: any; inpi: any; found_inpi: boolean; already_existed: boolean }> => {
    const { data } = await api.post('/v2/processos/lookup', payload);
    return data;
  },

  createInternationalProcess: async (payload: {
    code: string;
    domain: string;
    title?: string;
    status?: string;
    client_id?: string;
    country?: string;
    country_code?: string;
    process_number?: string;
    correspondent_id?: string;
    notes?: string;
    filing_date?: string;
    monitor_process?: boolean;
    monitor_collision?: boolean;
    monitor_market?: boolean;
    monitoring_types?: string[];
    is_third_party?: boolean;
  }): Promise<{ case: any; created: boolean }> => {
    const { data } = await api.post('/v2/processos/international', payload);
    return data;
  },

  // Get correspondents for dropdown
  getCorrespondents: async (): Promise<{ id: string; name: string; country_code?: string }[]> => {
    const { data } = await api.get('/v2/clientes?type=CORRESPONDENT&page=1&pageSize=200');
    return extractClientItems(data);
  },
};
